export * from "./player-form-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Select } from "@win-reality/react-ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { usaStates } from "typed-usa-states";
import { z } from "zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import {
  baseballPlayerPositionOptions,
  maxGradClassYear,
  minGradClassYear,
  playerFormDefaultValues,
  playerFormSchema,
  PlayerFormValues,
} from "./player-form-schema";

interface PlayerFormProps {
  values?: typeof playerFormDefaultValues;
  onSubmit?: (player: PlayerFormValues) => void;
  loading?: boolean;
  submitButtonLabel?: string;
  submittingButtonLabel?: string;
}

const PlayerForm = ({
  values = playerFormDefaultValues,
  submitButtonLabel = "Save",
  submittingButtonLabel = "Saving...",
  loading,
  onSubmit,
}: PlayerFormProps) => {
  const form = useForm<z.infer<typeof playerFormSchema>>({
    resolver: zodResolver(playerFormSchema),
    defaultValues: values,
  });

  const [submitting, setSubmitting] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [imageUrl, setImageUrl] = useState("https://placehold.co/250x333.png");
  const imageFileRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  useEffect(() => {
    setDisabled(Boolean(values.external_id) || submitting || Boolean(loading));
  }, [submitting, values.external_id, loading]);

  const onChangeImageClicked = useCallback(() => {
    if (imageFileRef.current && imageFileRef.current.files) {
      setImageUrl(URL.createObjectURL(imageFileRef.current.files[0]));
      //TODO upload file to the backend
    }
  }, [setImageUrl]);

  const onSubmitHandler = useCallback(
    async (values: PlayerFormValues) => {
      setSubmitting(true);

      try {
        setSubmitting(true);
        if (onSubmit) {
          onSubmit(values);
        }
      } catch (e: unknown) {
        //TODO handle errors
      } finally {
        setSubmitting(false);
      }
    },
    [onSubmit],
  );

  return (
    <Form {...form}>
      <div className="grid gap-0 lg:gap-4 grid-cols-1 lg:grid-cols-4">
        <div className="flex-col col-span-1 items-center justify-center">
          <div className="text-center flex items-center flex-col">
            <span className="font-lg">Profile Picture</span>
            <img
              src={imageUrl}
              alt="Player Avatar"
              className="rounded-lg h-auto max-w-full lg:max-w-72 object-cover aspect-[3/4]"
            />
          </div>
          <div className="text-center mt-3 mb-5 lg:mb-0">
            <Input
              type="file"
              className="hidden"
              onChange={onChangeImageClicked}
              ref={imageFileRef}
            />
            <Button
              size="sm"
              variant="secondary-flat-light"
              onClick={() =>
                imageFileRef.current && imageFileRef.current.click()
              }
            >
              Change Image
            </Button>
          </div>
        </div>
        <form
          onSubmit={form.handleSubmit(onSubmitHandler)}
          className="flex-col col-span-3"
        >
          <div className="grid gap-4 grid-cols-8">
            <FormField
              control={form.control}
              name="first_name"
              render={({ field }) => (
                <div className="flex flex-col col-span-8 lg:col-span-4 items-start">
                  <FormItem className="w-full">
                    <FormLabel>First Name:</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={disabled}
                        placeholder="First Name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <div className="flex flex-col col-span-8 lg:col-span-4 items-start">
                  <FormItem className="w-full">
                    <FormLabel>Last Name:</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={disabled}
                        placeholder="Last Name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <div className="flex flex-col col-span-8 lg:col-span-4 items-start">
                  <FormItem className="w-full">
                    <FormLabel>State:</FormLabel>
                    <FormControl>
                      <Select
                        items={usaStates.map((s) => ({
                          value: s.abbreviation,
                          label: s.name,
                        }))}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select a state..."
                        isDisabled={disabled}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <div className="flex flex-col col-span-8 lg:col-span-4 items-start">
                  <FormItem className="w-full">
                    <FormLabel>City:</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        disabled={disabled}
                        placeholder="City"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="primary_position"
              render={({ field }) => (
                <div className="flex flex-col col-span-8 lg:col-span-4 items-start">
                  <FormItem className="w-full">
                    <FormLabel>Primary Position:</FormLabel>
                    <FormControl>
                      <Select
                        items={[...baseballPlayerPositionOptions]}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select your primary position..."
                        isDisabled={disabled}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="secondary_position"
              render={({ field }) => (
                <div className="flex flex-col col-span-8 lg:col-span-4 items-start">
                  <FormItem className="w-full">
                    <FormLabel>Secondary Position:</FormLabel>
                    <FormControl>
                      <Select
                        items={[...baseballPlayerPositionOptions]}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select your secondary position..."
                        isDisabled={disabled}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="grad_class"
              render={({ field }) => (
                <div className="flex flex-col items-start col-span-8 lg:col-span-4">
                  <FormItem className="w-full">
                    <FormLabel>Graduation Year:</FormLabel>
                    <FormControl>
                      <Select
                        items={[
                          ...Array(maxGradClassYear - minGradClassYear + 1),
                        ].map((_, index) => ({
                          value: String(minGradClassYear + index),
                          label: String(minGradClassYear + index),
                        }))}
                        value={String(field.value)}
                        onValueChange={(value) => field.onChange(Number(value))}
                        placeholder="Select your graduation year..."
                        isDisabled={disabled}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                </div>
              )}
            />
          </div>
          <div className="flex justify-around mt-4">
            <div className="flex justify-center gap-4">
              <Button
                variant="ghost"
                type="button"
                onClick={() => navigate(-1)}
                isDisabled={disabled}
              >
                Cancel
              </Button>
              <Button
                variant="secondary-flat-light"
                type="submit"
                isDisabled={disabled}
                isLoading={submitting}
              >
                {submitting ? submittingButtonLabel : submitButtonLabel}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Form>
  );
};

export default PlayerForm;
